import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, BookOpen, Settings, User } from 'lucide-react';
import { useAuth } from '../auth';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const location = useLocation();
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const userMenuRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setShowUserMenu(false);
  }, [location]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    setShowUserMenu(false);
  };

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/programs', label: 'Programs' },
    { path: '/stories', label: 'Stories' },
    { path: '/science', label: 'Science' },
    { path: '/pricing', label: 'Pricing' },
    { path: '/about', label: 'About' },
  ];

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="navbar-container">
        <Link to="/" className="logo">
          Fasting <span>Carnivore</span>
        </Link>

        <ul className={`nav-links ${isOpen ? 'active' : ''}`}>
          {navLinks.map((link) => (
            <li key={link.path}>
              <Link
                to={link.path}
                className={location.pathname === link.path ? 'active' : ''}
              >
                {link.label}
              </Link>
            </li>
          ))}

          {/* Library link — visible in main nav when logged in */}
          {isAuthenticated && (
            <li>
              <Link
                to="/dashboard"
                className={`nav-library-link${location.pathname === '/dashboard' ? ' active' : ''}`}
              >
                My Library
              </Link>
            </li>
          )}

          {/* Auth Section */}
          {isAuthenticated ? (
            <>
              {/* Mobile: Show links directly */}
              <li className="mobile-only">
                <Link to="/dashboard">
                  <BookOpen size={18} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                  My Library
                </Link>
              </li>
              <li className="mobile-only">
                <Link to="/account">
                  <User size={18} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                  Account
                </Link>
              </li>
              {isAdmin && (
                <li className="mobile-only">
                  <Link to="/dashboard">
                    <Settings size={18} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                    Dashboard
                  </Link>
                </li>
              )}
              <li className="mobile-only">
                <button onClick={handleLogout} className="nav-logout-btn">
                  <LogOut size={18} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                  Sign Out
                </button>
              </li>

              {/* Desktop: User dropdown */}
              <li className="desktop-only user-menu-container" ref={userMenuRef}>
                <button
                  className="user-menu-trigger"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                >
                  <div className="user-avatar">
                    {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
                  </div>
                </button>

                {showUserMenu && (
                  <div className="user-dropdown">
                    <div className="user-dropdown-header">
                      <span className="user-name">{user?.name || 'User'}</span>
                      <span className="user-email">{user?.email}</span>
                    </div>
                    <div className="user-dropdown-divider" />
                    <Link to="/dashboard" className="user-dropdown-item">
                      <BookOpen size={16} />
                      My Library
                    </Link>
                    <Link to="/account" className="user-dropdown-item">
                      <User size={16} />
                      Account
                    </Link>
                    {isAdmin && (
                      <Link to="/dashboard" className="user-dropdown-item">
                        <Settings size={16} />
                        Dashboard
                      </Link>
                    )}
                    <div className="user-dropdown-divider" />
                    <button onClick={handleLogout} className="user-dropdown-item logout">
                      <LogOut size={16} />
                      Sign Out
                    </button>
                  </div>
                )}
              </li>
            </>
          ) : (
            <>
              <li>
                <Link to="/login" className="nav-login">
                  Sign In
                </Link>
              </li>
              <li>
                <Link to="/join" className="nav-cta">
                  Get started
                </Link>
              </li>
            </>
          )}
        </ul>

        <button
          className="mobile-toggle"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
    </nav>
  );
}
